import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Plus, Trash2, AlertTriangle, Gift, ToggleLeft, ToggleRight } from 'lucide-react';
import { api } from '../../api/client';
import { ConfirmDialog } from '../../components/ConfirmDialog';

type Starter = {
  id: string;
  productId: string;
  productName: string;
  guestCountMin: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

type Product = { id: string; name: string; type: string };

type Location = { id: string; name: string };

export function LocationStartersPage() {
  const { id: locationId } = useParams<{ id: string }>();
  const [location, setLocation] = useState<Location | null>(null);
  const [starters, setStarters] = useState<Starter[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<Starter | null>(null);

  // Create form state
  const [selectedProductId, setSelectedProductId] = useState('');
  const [guestCountMin, setGuestCountMin] = useState(2);
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    if (!locationId) return;
    setError(null);
    try {
      const [loc, s, p] = await Promise.all([
        api.get<Location>(`/admin/locations/${locationId}`),
        api.get<Starter[]>(`/admin/locations/${locationId}/starters`),
        /*
          Bez rezervnog poziva na `/products`.

          Ta ruta je **tenant** ruta: sysadmin token na njoj dobija 401, a
          klijentski sloj na svaki 401 briše prijavu - pa je otvaranje ove
          strane izbacivalo sysadmina na ekran za prijavu. Rezerva je uklonjena,
          a prava ruta (`/admin/locations/:id/products`) dodata na server.
        */
        api.get<Product[]>(`/admin/locations/${locationId}/products`),
      ]);
      setLocation(loc);
      setStarters(s);
      setProducts(p);
    } catch (err: any) {
      setError(err?.data?.message || err?.message || 'Greška pri učitavanju');
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleCreate = async () => {
    if (!selectedProductId || !locationId || guestCountMin < 1) return;
    setCreating(true);
    setError(null);
    try {
      await api.post<Starter>(`/admin/locations/${locationId}/starters`, {
        productId: selectedProductId,
        guestCountMin,
        isActive: true,
      });
      setSelectedProductId('');
      setGuestCountMin(2);
      await refresh();
    } catch (err: any) {
      setError(err?.data?.message || err?.message || 'Greška pri kreiranju');
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (starter: Starter) => {
    try {
      await api.put(`/admin/locations/${locationId}/starters/${starter.id}`, {
        isActive: !starter.isActive,
      });
      await refresh();
    } catch (err: any) {
      setError(err?.data?.message || err?.message || 'Greška pri izmeni');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.del(`/admin/locations/${locationId}/starters/${deleteTarget.id}`);
      setDeleteTarget(null);
      await refresh();
    } catch (err: any) {
      setError(err?.data?.message || err?.message || 'Greška pri brisanju');
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Učitavam...</div>;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Link to="/locations" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mb-2">
        <ChevronLeft size={16} /> Nazad na lokacije
      </Link>
      <div className="flex items-center gap-2 mb-6">
        <Gift size={24} className="text-purple-500" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Starters (auto-gratis stavke)</h1>
          <p className="text-sm text-gray-600">
            {location?.name ?? ''} - stavke koje se automatski dodaju u porudžbinu pri otvaranju stola
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 border border-red-300 text-red-700 text-sm flex items-center gap-2">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {/* Create form */}
      <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
        <h2 className="text-lg font-semibold mb-3 text-gray-900">Dodaj novo pravilo</h2>
        <div className="flex gap-3 items-end flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <label className="block text-xs text-gray-600 mb-1">Artikal</label>
            <select
              value={selectedProductId}
              onChange={(e) => setSelectedProductId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
            >
              <option value="">-- izaberi artikal --</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div className="w-32">
            <label className="block text-xs text-gray-600 mb-1">Min gostiju</label>
            <input
              type="number"
              min={1}
              max={50}
              value={guestCountMin}
              onChange={(e) => setGuestCountMin(parseInt(e.target.value, 10) || 1)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <button
            onClick={handleCreate}
            disabled={!selectedProductId || creating}
            className="px-4 py-2 bg-blue-600 text-white rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Plus size={16} />
            {creating ? 'Dodajem...' : 'Dodaj'}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          💡 Starter se auto-dodaje u porudžbinu kad se otvori sto sa <strong>≥ min gostiju</strong>.
          Unosi se kao gratis stavka (cena 0 RSD, note "STARTER").
        </p>
      </div>

      {/* List */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Aktivna pravila ({starters.length})
          </h2>
        </div>

        {starters.length === 0 ? (
          <div className="p-8 text-center text-gray-400">Nema konfigurisanih starter pravila.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Artikal</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Min gostiju</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">Akcije</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {starters.map((s) => (
                  <tr key={s.id} className={s.isActive ? '' : 'bg-gray-50 opacity-60'}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{s.productName}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">≥ {s.guestCountMin} gostiju</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${s.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                        {s.isActive ? 'Aktivan' : 'Neaktivan'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleToggle(s)}
                        className="p-1 text-gray-500 hover:text-blue-600"
                        title={s.isActive ? 'Deaktiviraj' : 'Aktiviraj'}
                      >
                        {s.isActive ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                      </button>
                      <button
                        onClick={() => setDeleteTarget(s)}
                        className="ml-2 p-1 text-red-500 hover:text-red-700"
                        title="Ukloni pravilo"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {deleteTarget && (
        <ConfirmDialog
          title="Obriši starter pravilo?"
          message={`"${deleteTarget.productName}" (≥${deleteTarget.guestCountMin} gostiju) će biti trajno uklonjen.`}
          confirmLabel="Obriši"
          variant="danger"
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
